<template>
  <div v-on-clickaway="closeSearch" data-testid="article-search-root">
    <div
      class="max-w-5xl w-full relative my-4"
      data-testid="article-search-input-wrapper"
    >
      <public-search-input
        id="search-input"
        v-model="searchTerm"
        :search-placeholder="searchTranslations.searchPlaceholder"
        :handle-enter="handleEnter"
        @focus="openSearch"
      />
      <div
        v-if="shouldShowSearchBox"
        class="absolute top-14 w-full z-50"
        @mouseover="openSearch"
      >
        <search-suggestions
          id="box-search"
          data-testid="article-search-suggestions"
          :items="searchResults"
          :is-loading="isLoading"
          :search-term="searchTerm"
          :empty-placeholder="searchTranslations.emptyPlaceholder"
          :results-title="searchTranslations.resultsTitle"
          :loading-placeholder="searchTranslations.loadingPlaceholder"
          :portal-slug="portalSlug"
        />
      </div>
    </div>

    <div
      v-if="isEnter"
      id="box-search-results"
      ref="searchResults"
      data-testid="article-search-results"
      :class="isSearchPage ? 'search-page-result' : 'custom-box-result'"
    >
      <div
        class="search-results-header mb-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-2"
      >
        <div class="search-breadcrumb text-slate-700">
          <span class="opacity-75"
            >{{ portalSlug.split('-').join(' ') }} >
          </span>
          <span class="font-bold text-slate-900"
            >Resultados de la búsqueda</span
          >
        </div>
        <div
          v-if="!isLoading && searchResults.length > 0"
          class="results-count text-slate-900 font-medium"
        >
          <span class="font-bold text-blue-900">{{ itemTotal }}</span>
          resultados para "<span class="font-bold text-blue-900">{{
            searchTerm
          }}</span
          >"
        </div>
      </div>

      <div
        :class="
          isSearchPage ? 'search-page-content' : 'custom-box-result-content'
        "
      >
        <div v-if="isLoading" class="text-center py-12">
          <div
            class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"
          />
          <div class="mt-2 text-blue-800 font-medium">
            {{ searchTranslations.loadingPlaceholder }}
          </div>
        </div>

        <div
          v-if="searchResults.length === 0 && !isLoading"
          class="bg-white rounded-xl shadow-sm p-8 text-center"
        >
          <h4 class="text-lg font-semibold text-slate-800">
            No hay resultados para "{{ searchTerm }}"
          </h4>
          <p class="text-slate-500 mt-2">
            Intenta con otros términos o revisa la ortografía.
          </p>
        </div>

        <div v-if="searchResults.length > 0 && !isLoading" class="space-y-4">
          <div
            v-if="summary && summary !== ''"
            id="search-summary-card"
            data-testid="article-search-summary-card"
            class="ai-summary-card"
          >
            <div class="ai-summary-badge">
              <svg
                class="ai-summary-icon"
                width="16"
                height="16"
                viewBox="0 0 15 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5.88417 5.88417C5.88417 5.88417 6.6197 0 7.35522 0C8.09074 0 8.82626 5.88417 8.82626 5.88417C8.82626 5.88417 14.7104 6.6197 14.7104 7.35522C14.7104 8.09074 8.82626 8.82626 8.82626 8.82626C8.82626 8.82626 8.09074 14.7104 7.35522 14.7104C6.6197 14.7104 5.88417 8.82626 5.88417 8.82626C5.88417 8.82626 -1.90735e-06 8.09074 -1.90735e-06 7.35522C-1.90735e-06 6.6197 5.88417 5.88417 5.88417 5.88417Z"
                  fill="white"
                />
                <path
                  d="M10.214 4.38549C10.214 4.38549 10.3112 3.60834 10.4083 3.60834C10.5055 3.60834 10.6026 4.38549 10.6026 4.38549C10.6026 4.38549 11.3798 4.48264 11.3798 4.57978C11.3798 4.67693 10.6026 4.77407 10.6026 4.77407C10.6026 4.77407 10.5055 5.55123 10.4083 5.55123C10.3112 5.55123 10.214 4.77407 10.214 4.77407C10.214 4.77407 9.43687 4.67693 9.43687 4.57978C9.43687 4.48264 10.214 4.38549 10.214 4.38549Z"
                  fill="#FFBE00"
                />
                <path
                  d="M2.22039 11.3799C2.22039 11.3799 2.49795 9.15942 2.7755 9.15942C3.05306 9.15942 3.33061 11.3799 3.33061 11.3799C3.33061 11.3799 5.55106 11.6574 5.55106 11.935C5.55106 12.2125 3.33061 12.4901 3.33061 12.4901C3.33061 12.4901 3.05306 14.7105 2.7755 14.7105C2.49795 14.7105 2.22039 12.4901 2.22039 12.4901C2.22039 12.4901 -5.05447e-05 12.2125 -5.05447e-05 11.935C-5.05447e-05 11.6574 2.22039 11.3799 2.22039 11.3799Z"
                  fill="#FFAA32"
                />
              </svg>
              <span>Respuesta generada con Inteligencia Artificial</span>
            </div>

            <div id="summary-content-container">
              <span id="summary-text-paragraph" v-dompurify-html="summary" />
              <div class="ai-suggestion-text">
                Sugerencia de AI basada en los mejores resultados de búsqueda
              </div>
            </div>

            <div class="ai-summary-feedback">
              <span class="feedback-question">¿Te sirvió esta respuesta?</span>
              <div v-if="!showThankYou" class="feedback-buttons">
                <button
                  data-testid="article-search-feedback-positive"
                  class="feedback-btn feedback-yes"
                  :class="{
                    'feedback-submitted': feedbackSubmitted === 'positive',
                  }"
                  @click="handleFeedback('positive')"
                >
                  <span class="thumbs-icon">👍</span>
                  Útil
                </button>
                <button
                  data-testid="article-search-feedback-negative"
                  class="feedback-btn feedback-no"
                  :class="{
                    'feedback-submitted': feedbackSubmitted === 'negative',
                  }"
                  @click="handleFeedback('negative')"
                >
                  <span class="thumbs-icon">👎</span>
                  No útil
                </button>
              </div>
              <div
                v-if="showThankYou"
                data-testid="article-search-feedback-thankyou"
                class="feedback-thank-you"
              >
                ¡Gracias por tu feedback!
              </div>
            </div>
          </div>

          <div
            v-for="article in searchResults"
            :id="article.id"
            :key="article.id"
            :data-testid="`article-search-result-${article.id}`"
            class="search-result-card bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-200 border border-transparent hover:border-l-4 hover:border-l-blue-500 cursor-pointer group"
            @click="navigateToArticle(article)"
          >
            <div class="flex flex-wrap justify-between items-start gap-4 mb-3">
              <div class="flex items-center gap-2 text-sm text-slate-500">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="text-blue-400"
                >
                  <path
                    d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"
                  />
                </svg>
                <span
                  >{{ portalSlug.split('-').join(' ') }} >
                  {{ article.category.name }}</span
                >
              </div>
            </div>

            <a :href="article.link" class="block mb-3">
              <h3
                v-dompurify-html="prepareContent(`${article.title}`)"
                class="text-xl font-bold text-slate-900 group-hover:text-blue-700 transition-colors"
              />
            </a>

            <div class="text-slate-600 text-sm leading-relaxed mb-4">
              <div
                v-dompurify-html="prepareContentFragment(`${article.content}`)"
                class="article-content-snippet"
              />
            </div>

            <div class="flex justify-end">
              <a
                :href="article.link"
                class="inline-flex items-center gap-1 font-bold text-sm read-more-link"
              >
                Leer más
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="pageTotal > 0"
        :class="
          isSearchPage ? 'search-page-footer' : 'custom-box-result-footer'
        "
      >
        <div class="btn-next">
          <button
            v-if="pageSearch > 1"
            data-testid="article-search-pagination-prev"
            class="btn-pagination btn-prev"
            @click="pageRemove"
          >
            <svg
              class="btn-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 19l-7-7 7-7"
              />
            </svg>
            <span>Anterior</span>
          </button>

          <div
            class="pagination-info"
            data-testid="article-search-pagination-info"
          >
            <span>Página {{ pageSearch }} de {{ pageTotal }}</span>
          </div>

          <button
            v-if="pageSearch < pageTotal"
            data-testid="article-search-pagination-next"
            class="btn-pagination btn-next"
            @click="pageAdd"
          >
            <span>Siguiente</span>
            <svg
              class="btn-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import publicArticleSearchMixin from './PublicArticleSearch/mixins/publicArticleSearchMixin';
import messageFormatterMixin from 'shared/mixins/messageFormatterMixin';
import SearchSuggestions from './SearchSuggestions.vue';
import PublicSearchInput from './PublicSearchInput.vue';

export default {
  name: 'PublicArticleSearch',
  components: {
    PublicSearchInput,
    SearchSuggestions,
  },
  mixins: [messageFormatterMixin, publicArticleSearchMixin],
  props: {
    value: {
      type: [String, Number],
      default: '',
    },
  },
};
</script>

<style
  src="./PublicArticleSearch/styles/PublicArticleSearch.scss"
  lang="scss"
></style>
